import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Plus, Edit, Shield, Trash2, Search, Mail } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Table, Column } from '../components/ui/Table';
import { formatDateTime } from '../utils/date';
import { Modal } from '../components/ui/Modal';
import { ConfirmDeleteModal } from '../components/ui/ConfirmDeleteModal';
import { Pagination } from '../components/ui/Pagination';
import { ProtectedAction } from '../components/ProtectedAction';
import { UserForm } from '../components/forms/UserForm';
import { RoleForm } from '../components/forms/RoleForm';
import { api } from '../services/api';
import { User, Role } from '../types';
import { fetchAllPagesData } from '../utils/fetchAllPages';

const limit = 20;

/** Clave interna para usuarios sin nombre de rol en el listado. */
const ROLE_TAB_SIN_ROL = '__sin_rol__';

export const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
  const [editingRole, setEditingRole] = useState<Role | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');
  /** Filtro por rol solo en cliente (sobre lista ya cargada). */
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');
  const [userModalError, setUserModalError] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string; users?: User[] }>({
    isOpen: false,
    message: '',
  });
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [roleModalError, setRoleModalError] = useState<string | null>(null);
  type PendingDelete =
    | { kind: 'user'; id: string; name: string }
    | { kind: 'role'; id: string; name: string };
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [roleDeleteLoading, setRoleDeleteLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleUserSearchChange = useCallback((value: string) => {
    setUserSearchTerm(value);
    setUserRoleFilter('all');
    setPage(1);
  }, []);

  const userSearchQuery = userSearchTerm.trim();
  const { data: usersRawList, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['users', 'directory', userSearchQuery],
    queryFn: () =>
      fetchAllPagesData((args) =>
        api.getUsers({
          page: args.page,
          limit: args.limit,
          search: userSearchQuery || undefined,
        }),
      ),
    staleTime: 60_000,
  });
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.getRoles(),
    staleTime: 60_000,
  });
  const roles = rolesData ?? [];
  const allUsers = useMemo(() => {
    const list = usersRawList ?? [];
    return (Array.isArray(list) ? list : []).map((user: any) => {
      const roleName = user.roles?.name || user.role?.name || user.roleName || '';
      return {
        ...user,
        roleName,
        roleId: user.roleId || user.roles?.id,
        createdAt: user.createdAt || new Date().toISOString(),
        lastLogin: user.lastLogin || undefined,
      } as User;
    });
  }, [usersRawList]);

  const roleTabKeys = useMemo(() => {
    const counts = new Map<string, number>();
    for (const u of allUsers) {
      const key = (u.roleName || '').trim() ? (u.roleName as string).trim() : ROLE_TAB_SIN_ROL;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const keys = [...counts.keys()].filter((k) => k !== ROLE_TAB_SIN_ROL || (counts.get(ROLE_TAB_SIN_ROL) ?? 0) > 0);
    keys.sort((a, b) => {
      if (a === ROLE_TAB_SIN_ROL) return 1;
      if (b === ROLE_TAB_SIN_ROL) return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
    return { counts, orderedRoleKeys: keys };
  }, [allUsers]);

  const filteredUsers = useMemo(() => {
    if (userRoleFilter === 'all') return allUsers;
    if (userRoleFilter === ROLE_TAB_SIN_ROL) {
      return allUsers.filter((u) => !(u.roleName || '').trim());
    }
    return allUsers.filter((u) => (u.roleName || '').trim() === userRoleFilter);
  }, [allUsers, userRoleFilter]);

  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const users = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredUsers.slice(start, start + limit);
  }, [filteredUsers, page]);
  const queryErrorMessage =
    queryError != null
      ? (queryError instanceof Error ? queryError.message : String(queryError))
      : null;
  const error = mutationError || queryErrorMessage;

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(1, totalPages)));
  }, [totalPages]);

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateUser(id, data),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setEditingUser(undefined);
      setIsUserModalOpen(false);
      setUserModalError(null);
    },
    onError: (err: Error) => setUserModalError(err.message || 'Error al guardar el usuario'),
  });

  const createUserMutation = useMutation({
    mutationFn: (data: any) => api.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setPage(1);
      setUserRoleFilter('all');
      setIsUserModalOpen(false);
      setUserModalError(null);
    },
    onError: (err: Error) => setUserModalError(err.message || 'Error al guardar el usuario'),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const resendAccessMutation = useMutation({
    mutationFn: (id: string) => api.resendUserAccessEmail(id),
    onMutate: () => {
      setSuccessMessage(null);
      setMutationError(null);
    },
    onSuccess: (data) => {
      setMutationError(null);
      setSuccessMessage(data.message);
    },
    onError: (err: Error) => {
      setMutationError(err.message || 'No se pudo enviar el correo de acceso');
    },
  });

  const resendPendingFor = (id: string) =>
    resendAccessMutation.isPending && resendAccessMutation.variables === id;

  const columns: Column<User>[] = [
    { header: 'Nombre', accessor: 'name', sortable: true },
    { header: 'Email', accessor: 'email', sortable: true },
    { header: 'Teléfono', accessor: 'phone' },
    {
      header: 'Rol',
      accessor: (row) => (
        <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
          {row.roleName}
        </span>
      ),
    },
    {
      header: 'Estado',
      accessor: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {row.status === 'active' ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      header: 'Último inicio de sesión',
      accessor: (row) =>
        row.lastLogin ? (
          <span title="Fecha y hora del último login exitoso en el sistema">
            {formatDateTime(row.lastLogin)}
          </span>
        ) : (
          <span
            className="text-gray-500"
            title="Este usuario aún no ha iniciado sesión con su cuenta"
          >
            Sin ingresos
          </span>
        ),
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <div className="flex items-center space-x-2">
          <ProtectedAction permission={['update_users', 'manage_users']}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditingUser(row);
                setIsUserModalOpen(true);
              }}
              className="text-primary hover:text-primary/80 p-1"
              title="Editar usuario"
              aria-label="Editar usuario"
            >
              <Edit className="w-4 h-4" />
            </button>
          </ProtectedAction>
          <ProtectedAction permission={['update_users', 'manage_users']}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                resendAccessMutation.mutate(row.id);
              }}
              disabled={row.status !== 'active' || resendPendingFor(row.id)}
              className="text-sky-600 hover:text-sky-800 disabled:opacity-40 disabled:pointer-events-none p-1"
              title={
                row.status !== 'active'
                  ? 'Solo usuarios activos'
                  : 'Enviar por correo una contraseña temporal y el enlace de acceso'
              }
              aria-label="Reenviar acceso por correo"
            >
              {resendPendingFor(row.id) ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-sky-600 border-t-transparent" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
            </button>
          </ProtectedAction>
          <ProtectedAction permission="delete_users">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteUser(row.id);
              }}
              className="text-red-600 hover:text-red-800 p-1"
              title="Eliminar usuario"
              aria-label="Eliminar usuario"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </ProtectedAction>
        </div>
      ),
    },
  ];

  const roleColumns: Column<Role>[] = [
    { header: 'Nombre', accessor: 'name', sortable: true },
    { header: 'Descripción', accessor: 'description' },
    {
      header: 'Usuarios',
      className: 'text-right tabular-nums w-px whitespace-nowrap',
      accessor: (row) => (
        <span className="font-medium text-gray-900">{row.userCount ?? 0}</span>
      ),
    },
    {
      header: 'Permisos',
      accessor: (row) => (
        <span className="text-sm text-gray-600">{row.permissions.length} permisos</span>
      ),
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <div className="flex items-center space-x-2">
          <ProtectedAction permission={['update_roles', 'manage_settings']}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditingRole(row);
                setIsRoleModalOpen(true);
              }}
              className="text-primary hover:text-primary/80 p-1"
              title="Editar rol"
              aria-label="Editar rol"
            >
              <Edit className="w-4 h-4" />
            </button>
          </ProtectedAction>
          <ProtectedAction permission="delete_roles">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRole(row.id);
              }}
              className="text-red-600 hover:text-red-800 p-1"
              title="Eliminar rol"
              aria-label="Eliminar rol"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </ProtectedAction>
        </div>
      ),
    },
  ];

  const handleOpenUserModal = (user?: User) => {
    setEditingUser(user);
    setUserModalError(null);
    setMutationError(null);
    setSuccessMessage(null);
    setIsUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setEditingUser(undefined);
    setUserModalError(null);
  };

  const handleUserSubmit = (userData: any) => {
    setUserModalError(null);
    if (editingUser) {
      updateUserMutation.mutate({ id: editingUser.id, data: userData });
    } else {
      createUserMutation.mutate(userData);
    }
  };

  const handleDeleteUser = (id: string) => {
    const u = allUsers.find((x) => x.id === id);
    setMutationError(null);
    setPendingDelete({ kind: 'user', id, name: u?.name || 'este usuario' });
  };

  const handleConfirmDeleteUser = () => {
    if (!pendingDelete || pendingDelete.kind !== 'user') return;
    deleteUserMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: (err: Error) => {
        setMutationError(err.message || 'Error al eliminar el usuario');
        setPendingDelete(null);
      },
    });
  };

  const handleOpenRoleModal = (role?: Role) => {
    setEditingRole(role);
    setRoleModalError(null);
    setIsRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setIsRoleModalOpen(false);
    setEditingRole(undefined);
    setRoleModalError(null);
  };

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Omit<Role, 'id' | 'createdAt'> }) => api.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      handleCloseRoleModal();
    },
    onError: (err: Error) => setRoleModalError(err.message || 'Error al guardar el rol'),
  });

  const createRoleMutation = useMutation({
    mutationFn: (data: Omit<Role, 'id' | 'createdAt'>) => api.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      handleCloseRoleModal();
    },
    onError: (err: Error) => setRoleModalError(err.message || 'Error al guardar el rol'),
  });

  const handleRoleSubmit = (roleData: Omit<Role, 'id' | 'createdAt'>) => {
    setRoleModalError(null);
    setMutationError(null);
    if (editingRole) {
      updateRoleMutation.mutate({ id: editingRole.id, data: roleData });
    } else {
      createRoleMutation.mutate(roleData);
    }
  };

  const handleDeleteRole = (id: string) => {
    const role = roles.find((r) => r.id === id);
    if (!role) return;
    setMutationError(null);
    setPendingDelete({ kind: 'role', id, name: role.name });
  };

  const handleConfirmDeleteRole = () => {
    if (!pendingDelete || pendingDelete.kind !== 'role') return;
    const { id, name } = pendingDelete;
    setRoleDeleteLoading(true);
    setMutationError(null);
    api
      .deleteRole(id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['roles'] });
        setPendingDelete(null);
      })
      .catch((err: any) => {
        const errorMessage = err.message || '';
        if (
          errorMessage.includes('en uso') ||
          errorMessage.includes('utilizado') ||
          errorMessage.includes('usado') ||
          errorMessage.includes('usuario(s)')
        ) {
          const usersWithRole = allUsers.filter((u) => u.roleId === id);
          setErrorModal({
            isOpen: true,
            message: `No se puede eliminar el rol "${name}" porque está siendo utilizado por ${usersWithRole.length} usuario(s).`,
            users: usersWithRole,
          });
        } else {
          setMutationError(errorMessage || 'Error al eliminar el rol');
        }
        setPendingDelete(null);
      })
      .finally(() => setRoleDeleteLoading(false));
  };

  const handleCloseErrorModal = () => {
    setErrorModal({ isOpen: false, message: '', users: undefined });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Settings className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
        </div>
        <div className="flex space-x-2">
          {activeTab === 'users' && (
            <ProtectedAction permission={['create_users', 'manage_users']}>
              <Button icon={Plus} onClick={() => handleOpenUserModal()}>
                Crear Usuario
              </Button>
            </ProtectedAction>
          )}
          {activeTab === 'roles' && (
            <ProtectedAction permission={['create_roles', 'manage_settings']}>
              <Button icon={Shield} onClick={() => handleOpenRoleModal()}>
                Crear Rol
              </Button>
            </ProtectedAction>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Usuarios ({allUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roles'
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Roles ({roles.length})
          </button>
        </nav>
      </div>

      {/* Error Message (no mostrar fuera cuando un modal está abierto; los errores van dentro de cada modal) */}
      {successMessage && activeTab === 'users' && !isUserModalOpen && !isRoleModalOpen && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg flex justify-between gap-3 items-start">
          <span>{successMessage}</span>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            className="text-emerald-700 hover:text-emerald-900 shrink-0 text-sm font-medium"
          >
            Cerrar
          </button>
        </div>
      )}
      {error && !isUserModalOpen && !isRoleModalOpen && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'users' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
            <input
              type="text"
              value={userSearchTerm}
              onChange={(e) => handleUserSearchChange(e.target.value)}
              placeholder="Buscar por nombre, correo, teléfono o rol..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              aria-label="Buscar usuarios"
            />
          </div>

          <div className="overflow-x-auto -mx-1 px-1">
            <div
              className="flex w-max min-w-full gap-1 border-b border-gray-200 pb-px sm:flex-wrap sm:w-auto sm:gap-2"
              role="tablist"
              aria-label="Filtrar usuarios por rol"
            >
              <button
                type="button"
                role="tab"
                aria-selected={userRoleFilter === 'all'}
                onClick={() => {
                  setUserRoleFilter('all');
                  setPage(1);
                }}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-t-md border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                  userRoleFilter === 'all'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'
                }`}
              >
                Todos
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
                  {allUsers.length}
                </span>
              </button>
              {roleTabKeys.orderedRoleKeys.map((roleKey) => {
                const count = roleTabKeys.counts.get(roleKey) ?? 0;
                const label = roleKey === ROLE_TAB_SIN_ROL ? 'Sin rol' : roleKey;
                return (
                  <button
                    key={roleKey}
                    type="button"
                    role="tab"
                    aria-selected={userRoleFilter === roleKey}
                    onClick={() => {
                      setUserRoleFilter(roleKey);
                      setPage(1);
                    }}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-t-md border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                      userRoleFilter === roleKey
                        ? 'border-accent text-accent'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'
                    }`}
                  >
                    {label}
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <Table data={users} columns={columns} />
          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {activeTab === 'roles' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
            <input
              type="text"
              value={roleSearchTerm}
              onChange={(e) => setRoleSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o descripción..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              aria-label="Buscar roles"
            />
          </div>
          <Table
            data={roles.filter((r) => {
              if (!roleSearchTerm.trim()) return true;
              const term = roleSearchTerm.toLowerCase().trim();
              return (
                r.name?.toLowerCase().includes(term) ||
                r.description?.toLowerCase().includes(term)
              );
            })}
            columns={roleColumns}
          />
        </div>
      )}

      {/* Modal para Usuario */}
      <Modal
        isOpen={isUserModalOpen}
        onClose={handleCloseUserModal}
        title={editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        size="lg"
      >
        {userModalError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {userModalError}
          </div>
        )}
        <UserForm
          user={editingUser}
          roles={roles}
          onSubmit={handleUserSubmit}
          onCancel={handleCloseUserModal}
          isSubmitting={createUserMutation.isPending || updateUserMutation.isPending}
        />
      </Modal>

      {/* Modal para Rol */}
      <Modal
        isOpen={isRoleModalOpen}
        onClose={handleCloseRoleModal}
        title={editingRole ? 'Editar Rol' : 'Crear Nuevo Rol'}
        size="lg"
      >
        <RoleForm
          role={editingRole}
          onSubmit={handleRoleSubmit}
          onCancel={handleCloseRoleModal}
          isSubmitting={updateRoleMutation.isPending || createRoleMutation.isPending}
          serverError={roleModalError}
          onDismissServerError={() => setRoleModalError(null)}
        />
      </Modal>

      <ConfirmDeleteModal
        isOpen={pendingDelete !== null}
        onClose={() => {
          if (!deleteUserMutation.isPending && !roleDeleteLoading) setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete?.kind === 'user') handleConfirmDeleteUser();
          else if (pendingDelete?.kind === 'role') handleConfirmDeleteRole();
        }}
        message={
          pendingDelete?.kind === 'user' ? (
            <>
              ¿Seguro que deseas eliminar al usuario <strong>{pendingDelete.name}</strong>?
            </>
          ) : pendingDelete?.kind === 'role' ? (
            <>
              ¿Seguro que deseas eliminar el rol <strong>{pendingDelete.name}</strong>?
            </>
          ) : null
        }
        isLoading={
          (pendingDelete?.kind === 'user' && deleteUserMutation.isPending) ||
          (pendingDelete?.kind === 'role' && roleDeleteLoading)
        }
      />

      {/* Modal de Error - Rol en Uso */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={handleCloseErrorModal}
        title="No se puede eliminar el rol"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 mb-4">
                {errorModal.message}
              </p>
              
              {errorModal.users && errorModal.users.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Usuarios que están usando este rol:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <ul className="space-y-2">
                      {errorModal.users.map((user) => (
                        <li key={user.id} className="flex items-center space-x-2 text-sm text-gray-700">
                          <span className="w-2 h-2 bg-primary rounded-full"></span>
                          <span className="font-medium">{user.name}</span>
                          <span className="text-gray-500">({user.email})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Por favor, asigna un rol diferente a estos usuarios antes de eliminar este rol.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={handleCloseErrorModal}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
